import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  Avatar,
  Badge,
  Button,
  Alert,
  Divider,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import ThemeToggle from "./ThemeToggle";
import {
  KeyboardArrowDown,
  Notifications,
  ExitToApp,
  Settings,
  Lock,
  QueryStats,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { removeUser } from "../../../Redux/Actions/UserActions";
import { requestMarketData } from "../../../Redux/Reducers/marketDataSlice";
import {
  updateLoadingMsg,
} from "../../../Redux/Reducers/uiSlice";
import { setShowMarketLoader } from "../../../Redux/Reducers/marketDataSlice";
import { getMarketDataThreshold } from "../../../Redux/Reducers/selectors";
import Popup from "../Popup";
import Sync from "../Topbar/Sync";
import ChangePassword from "../Topbar/ChangePassword";
import AlignVerticalCenterIcon from "@mui/icons-material/AlignVerticalCenter";
import Blacklist from "../Popup/Manager/Blacklist";
import {
  sendWebSocketMessage,
  useWebSocket,
} from "../../../hooks/useWebSocket";
import { selectLots, selectStrategies, selectManagers, selectNotifications, selectAccountingData, selectIsStrategist, selectIsInvestor, selectLivePrices } from "../../../Redux/Reducers/selectors";

const TopNavigation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSync, setIsSync] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);
  const [isBlacklist, setIsBlacklist] = useState(false);
  const [count, setCount] = useState(0);
  const { user, userInfo } = useSelector((state) => state?.users);
  const livePrices = useSelector(selectLivePrices);
  const isStrategist = useSelector(selectIsStrategist);
  const isInvestor = useSelector(selectIsInvestor);
  const accountingData = useSelector(selectAccountingData);
  const notifications = useSelector(selectNotifications);
  const managers = useSelector(selectManagers);
  const lots = useSelector(selectLots);
  const strategies = useSelector(selectStrategies);
  const marketDataThreshold = useSelector(getMarketDataThreshold);
  const { disconnect } = useWebSocket();
  useEffect(() => {
    if (notifications && Array.isArray(notifications)) {
      const unreadCount = notifications.filter(
        (notification) => !notification?.isRead
      ).length;
      setCount(unreadCount);
    } else {
      setCount(0);
    }
  }, [notifications]);

  // Navigation items based on user role
  const getNavigationItems = () => {
    const items = [];
    if (isStrategist) {
      items.push(
        { title: "Performance", path: "/portfolio", children: [] },
        { title: "Strategies", path: "/strategies", children: [] },
        {
          title: "Signals",
          path: "/signals",
          children: [
            { title: "Signals", path: "/signals" },
            { title: "Historical Signals", path: "/historical-signals" },
          ],
        }
      );
    } else {
      items.push(
        {
          title: "Performance",
          path: "/portfolio",
          children: [
            { title: "Portfolio", path: "/portfolio" },
            { title: "Performance Dashboard", path: "/vantage" },
            { title: "Positions", path: "/positions" },
          ],
        },
        {
          title: "EOD Reports",
          path: "/reports",
          children: [],
        },
        {
          title: "Trade Blotter",
          path: "/tradesV2",
          children: [],
        },
        {
          title: "Fund Allocator",
          path: "/fundAllocator",
          children: [],
        },
        {
          title: "Signals",
          path: "/signals",
          children: [
            { title: "Signals", path: "/signals" },
            { title: "Historical Signals", path: "/historical-signals" },
          ],
        },
        {
          title: "Blacklist",
          path: "/blacklist",
          children: [
            { title: "Funds Blacklist", path: "/funds-blacklist" },
            // { title: "Manager Blacklist", path: "/manager-blacklist" },
          ],
        },
        {
          title: "Rebalancer",
          path: "/rebalancer",
          children: [],
        },
        {
          title: "Entities",
          path: "/entities",
          children: [
            { title: "Accounting", path: "/accounting" },
            { title: "Brokers", path: "/broker" },
            { title: "Custodians", path: "/custodian" },
            { title: "Funds", path: "/funds" },
            { title: "Strategies", path: "/strategies" },
            { title: "Security Master", path: "/security" },
          ],
        }
      );
    }
    return items;
  };

  const navigationItems = getNavigationItems();
  const [openMenus, setOpenMenus] = useState({});

  const handleMenuOpen = (event, itemTitle) => {
    setOpenMenus({ ...openMenus, [itemTitle]: event.currentTarget });
  };

  const handleMenuClose = (itemTitle) => {
    setOpenMenus({ ...openMenus, [itemTitle]: null });
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Close all menus
    setOpenMenus({});
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePass = () => {
    handleUserMenuClose();
    setIsChangePassword(true);
  };

  const handleBlacklist = () => {
    handleUserMenuClose();
    setIsBlacklist(true);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    dispatch(removeUser());
    disconnect();
    navigate("/login");
  };

  const handleSettings = () => {
    handleUserMenuClose();
    navigate("/settings");
  };

  const handleNotification = () => {
    sendWebSocketMessage(`{"action":"modify_notifications"}`);
    navigate("/notifications");
  };

  const handleMarketData = () => {
    dispatch(updateLoadingMsg("Fetching Market Data"));
    dispatch(setShowMarketLoader(true));
    dispatch(
      requestMarketData({
        lots: lots,
        threshold: marketDataThreshold,
        market: "fmp",
      })
    );

    const timeoutDuration = livePrices?.length > 0 ? 30000 : 60000;
    setTimeout(() => {
      dispatch(updateLoadingMsg("Please Wait..."));
      dispatch(setShowMarketLoader(false));
    }, timeoutDuration);
  };

  const getUserAvatar = () => {
    const initials =
      (userInfo?.firstName?.charAt(0) || "") +
      (userInfo?.lastName?.charAt(0) || "");

    return (
      <Avatar
        alt="user-initials"
        sx={{
          border: `1px solid #272727`,
          cursor: "pointer",
          width: "40px",
          height: "40px",
        }}
      >
        {initials}
      </Avatar>
    );
  };

  const formatSyncDate = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(parseInt(timestamp));
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options).replace(/\//g, "-");
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return "Never synced";

    const now = new Date();
    const syncDate = new Date(parseInt(timestamp));
    const diffMs = now - syncDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  };

  const isSyncRecent = (timestamp) => {
    if (!timestamp) return false;
    const syncDate = new Date(parseInt(timestamp));
    const now = new Date();
    const diffHours = (now - syncDate) / (1000 * 60 * 60);

    // If today is Monday, allow up to 72 hours
    if (now.getDay() === 1) {
      // Monday is 1
      return diffHours <= 60;
    }
    // Otherwise, allow up to 24 hours
    return diffHours <= 24;
  };

  // Sync stage mappings
  const ManagerSyncStage = {
    Completed: 0,
    Processing: 1,
    Started: 2
  };

  const AccountingSyncStage = {
    Completed: 0,
    Downloading: 1,
    Processing: 2,
    Started: 3
  };

  const getStageText = (stage, isRecent, isAccounting = false) => {
    if (isAccounting) {
      switch (stage) {
        case AccountingSyncStage.Completed: return isRecent ? "✓" : "⚠";
        case AccountingSyncStage.Downloading: return "↓";
        case AccountingSyncStage.Processing: return "⚙";
        case AccountingSyncStage.Started: return "▶";
        default: return "?";
      }
    } else {
      switch (stage) {
        case ManagerSyncStage.Completed: return isRecent ? "✓" : "⚠";
        case ManagerSyncStage.Processing: return "⚙";
        case ManagerSyncStage.Started: return "▶";
        default: return "?";
      }
    }
  };

  const getStageColor = (stage, isRecent, isAccounting = false) => {
    if (!isRecent) return "#ff9800"; // Orange for old sync
    
    if (isAccounting) {
      switch (stage) {
        case AccountingSyncStage.Completed: return "#4caf50"; // Green
        case AccountingSyncStage.Downloading: return "#2196f3"; // Blue
        case AccountingSyncStage.Processing: return "#ff9800"; // Orange
        case AccountingSyncStage.Started: return "#9c27b0"; // Purple
        default: return "#f44336"; // Red
      }
    } else {
      switch (stage) {
        case ManagerSyncStage.Completed: return "#4caf50"; // Green
        case ManagerSyncStage.Processing: return "#ff9800"; // Orange
        case ManagerSyncStage.Started: return "#9c27b0"; // Purple
        default: return "#f44336"; // Red
      }
    }
  };

  const CompactSyncStatus = () => {
    const internalSync = managers[0]?.sync;
    const accountingSync = accountingData?.[0]?.sync;
    
    const internalRecent = isSyncRecent(internalSync?.date);
    const accountingRecent = isSyncRecent(accountingSync?.date);
    
    const internalStageColor = getStageColor(internalSync?.stage, internalRecent, false);
    const accountingStageColor = getStageColor(accountingSync?.stage, accountingRecent, true);
    
    const internalStageText = getStageText(internalSync?.stage, internalRecent, false);
    const accountingStageText = getStageText(accountingSync?.stage, accountingRecent, true);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          minWidth: "fit-content",
        }}
      >
        {/* Internal Sync */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
          <Tooltip
            title={`Internal Sync: ${internalStageText} (${getRelativeTime(internalSync?.date)})`}
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: internalStageColor,
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              },
              arrow: {
                sx: {
                  color: internalStageColor,
                },
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: internalStageColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
                lineHeight: 1,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              {internalStageText}
            </Box>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: theme.palette.text.secondary,
              fontWeight: 600,
            }}
          >
            Internal Sync
          </Typography>
        </Box>

        {/* Accounting Sync */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip
            title={`Accounting Sync: ${accountingStageText} (${getRelativeTime(accountingSync?.date)})`}
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: accountingStageColor,
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              },
              arrow: {
                sx: {
                  color: accountingStageColor,
                },
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: accountingStageColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
                lineHeight: 1,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              {accountingStageText}
            </Box>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: theme.palette.text.secondary,
              fontWeight: 600,
            }}
          >
            Accounting Sync
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
    className="top-navigation"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      {/* Top Navigation Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1,
          minHeight: "64px",
        }}
      >
        {/* Logo and Navigation Items */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "24px",
                color: theme.palette.primary.main,
                textTransform: "uppercase",
              }}
            >
              Vantage
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "300",
                fontSize: "24px",
                color: theme.palette.primary.main,
                ml: 0.5,
                textTransform: "uppercase",
              }}
            >
              Alpha
            </Typography>
          </Box>

          {/* Navigation Items */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {navigationItems.map((item) => (
              <Box key={item.title} sx={{ position: "relative" }}>
                <Button
                  onClick={(e) => {
                    if (item.children.length === 0) {
                      handleNavigation(item.path);
                    } else {
                      handleMenuOpen(e, item.title);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (item.children.length > 0) {
                      handleMenuOpen(e, item.title);
                    }
                  }}
                  sx={{
                    color: location.pathname.startsWith(item.path)
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: "normal",
                    borderRadius: 0,
                    px: 2,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.primary.main,
                    },
                  }}
                  endIcon={
                    item.children.length > 0 ? <KeyboardArrowDown /> : null
                  }
                >
                  {item.title}
                </Button>

                {/* Dropdown Menu */}
                {item.children.length > 0 && (
                  <Menu
                    anchorEl={openMenus[item.title]}
                    open={Boolean(openMenus[item.title])}
                    onClose={() => handleMenuClose(item.title)}
                    onMouseLeave={() => handleMenuClose(item.title)}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: theme.shadows[8],
                        borderRadius: 2,
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    {item.children.map((child) => (
                      <MenuItem
                        key={child.title}
                        onClick={() => handleNavigation(child.path)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Typography variant="body2">{child.title}</Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right Side - Notifications, User Menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Sync Status */}
          {!isStrategist && !isInvestor && managers?.length > 0 && (
            <CompactSyncStatus />
          )}

          {/* Market Data */}
          <Tooltip title="Fetch Market Data" arrow placement="bottom">
            <IconButton
              onClick={handleMarketData}
              sx={{ color: theme.palette.text.primary }}
            >
              <QueryStats />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          {/* {!isStrategist && !isInvestor && (
            <IconButton
              onClick={handleNotification}
              sx={{ color: theme.palette.text.primary }}
            >
              <Badge badgeContent={count} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          )} */}

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User Menu */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={handleUserMenuClick}
          >
            {getUserAvatar()}
            {/* <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {`${userInfo?.firstName} ${userInfo?.lastName}`}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {isStrategist ? "Strategist" : managers[0]?.name || ""}
              </Typography>
            </Box> */}
            <KeyboardArrowDown />
          </Box>

          {/* User Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: theme.shadows[8],
                borderRadius: 2,
              },
            }}
          >
           
            <MenuItem onClick={handleChangePass} sx={{  }}>
              <ListItemIcon sx={{ zoom: 0.75 }}>
                <Lock />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: "error.main", }}>
              <ListItemIcon sx={{ zoom: 0.75 }}>
                <ExitToApp sx={{ color: "error.main" }} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Popups */}
      <Popup
        title="SYNC DATA ITEMS"
        openPopup={isSync}
        setOpenPopup={setIsSync}
      >
        <Sync setIsSync={setIsSync} />
      </Popup>
      <Popup
        title="Change Password"
        openPopup={isChangePassword}
        setOpenPopup={setIsChangePassword}
      >
        <ChangePassword setOpenPopup={setIsChangePassword} />
      </Popup>
      <Blacklist
        managers={managers}
        strategies={strategies}
        openBlacklistPopup={isBlacklist}
        setBlacklistOpenPopup={setIsBlacklist}
        title="Manager Blacklist"
      />
    </Box>
  );
};

export default TopNavigation;
